/** Control/multi-line helpers: wire (potential) numbering and terminal numbering. */
import { sheetsInOrder, type Id, type Op, type Project } from '@elec/domain';
import { connectivity } from '@elec/analysis';
import { wirePolyline } from '@elec/renderer';

/** Labels that name a potential rather than a wire number. */
export const POTENTIAL_RE = /^(L\d?|N|PE|PEN|\+?\d+\s?V(?:AC|DC)?|0\s?V|M)$/i;
const AUTO_RE = /^\d+$/;

/**
 * Number the conductors of a sheet: one number per electrical net (all wires joined by
 * junctions share it), shown once on the longest wire of the net. Nets carrying a potential
 * label (L, N, PE, 24V…) keep it. Hand-written labels are kept; only empty or numeric labels
 * are (re)assigned. Numbering starts at `start` and follows the drawing, left to right.
 */
export function numberWiresOps(project: Project, sheetId: Id, start = 1): Op[] {
  const net = connectivity(project, sheetId);
  const wires = Object.values(project.wires).filter((w) => w.sheetId === sheetId);
  // Group wires by net (via either end).
  const groups = new Map<string, typeof wires>();
  const keyOf = (w: (typeof wires)[number]) => {
    const end = w.from.kind === 'port' ? w.from : w.to.kind === 'port' ? w.to : null;
    if (end) return net.netOf(end.elementId, end.portId);
    return `w${w.id}`;
  };
  // Free-standing wires (both ends points) join the net of any wire they touch: use
  // connectivity's grouping through wiresIn.
  const seen = new Set<Id>();
  for (const w of wires) {
    if (seen.has(w.id)) continue;
    const k = keyOf(w);
    const members = k.startsWith('w') ? [w] : net.wiresIn(k);
    const list = members.length ? members : [w];
    for (const m of list) seen.add(m.id);
    groups.set(k, list);
  }
  const pts = (w: (typeof wires)[number]) => wirePolyline(project, w) ?? [];
  const minX = (ws: typeof wires) => Math.min(...ws.flatMap((w) => pts(w).map((p) => p.x)));
  const minY = (ws: typeof wires) => Math.min(...ws.flatMap((w) => pts(w).map((p) => p.y)));
  const ordered = [...groups.values()].sort((a, b) => minX(a) - minX(b) || minY(a) - minY(b));
  const ops: Op[] = [];
  let n = start;
  for (const ws of ordered) {
    if (ws.some((w) => w.label && POTENTIAL_RE.test(w.label.trim()))) continue;
    if (ws.some((w) => w.label && !AUTO_RE.test(w.label.trim()))) continue; // user-named net
    const len = (w: (typeof ws)[number]) => {
      const p = pts(w);
      let d = 0;
      for (let i = 0; i < p.length - 1; i++) d += Math.hypot(p[i + 1]!.x - p[i]!.x, p[i + 1]!.y - p[i]!.y);
      return d;
    };
    const longest = ws.reduce((a, b) => (len(b) > len(a) ? b : a));
    const label = String(n++);
    for (const w of ws) {
      const want = w.id === longest.id ? label : undefined;
      if ((w.label ?? undefined) !== want)
        ops.push({ op: 'update', coll: 'wires', id: w.id, patch: { label: want } });
    }
  }
  return ops;
}

/** Number terminals 1…n within each strip (same tag, e.g. X1), in drawing order across sheets. */
export function numberTerminalsOps(project: Project): Op[] {
  const order = new Map(sheetsInOrder(project).map((s, i) => [s.id, i]));
  const strips = new Map<string, Array<(typeof project.elements)[string]>>();
  for (const e of Object.values(project.elements)) {
    if (e.symbolId !== 'iec.terminal' || !e.tag) continue;
    strips.set(e.tag, [...(strips.get(e.tag) ?? []), e]);
  }
  const ops: Op[] = [];
  for (const list of strips.values()) {
    list.sort((a, b) => (order.get(a.sheetId) ?? 0) - (order.get(b.sheetId) ?? 0) || a.x - b.x || a.y - b.y);
    list.forEach((e, i) => {
      const number = String(i + 1);
      if (e.props.number !== number)
        ops.push({ op: 'update', coll: 'elements', id: e.id, patch: { props: { ...e.props, number } } });
    });
  }
  return ops;
}
